import React, { useEffect, useState, useContext } from "react";
import { CPContext } from "../../_helpers/CPContext";
import { useOktaAuth } from "@okta/okta-react";
import { useHistory } from "react-router-dom";
import * as utilities from "../../_helpers/utilities";
import * as logger from "../../_helpers/logger";
import { config } from "../../config";
import { Card } from "react-bootstrap";
import { Spinner } from "../shared/Common";
import { pdfjs, Document, Page } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
const options = {
  cMapUrl: "/cmaps/",
  standardFontDataUrl: "/standard_fonts/",
};

const DocumentDeliveryAuthorization: React.FC = () => {
  const { oktaAuth } = useOktaAuth();
  const cpContext = useContext(CPContext);
  const navigate = useHistory();
  const [eAuthPDFBlob, setEAuthPDFBlob] = useState<string>();
  const [errorMessage, setErrorMessage] = useState("");
  const [pdfLoaded, setPDFLoaded] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [numPages, setNumPages] = useState<number>();
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  let lastScrollTop = 0;

  useEffect(() => {
    getEAuthPreview();
  }, []);

  const getEAuthPreview = async () => {
    try {
      const body: fileDownloadRequest = {
        id: 0,
        policyDWKey: 0,
        ignorePreviousDisclosure: true,
      };
      const requestOptionsBodyAuth = {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + (oktaAuth.getAccessToken() || ""),
        },
      };

      await fetch(
        config.resourceServer.apiUrl + "/policy/eauthpreviews",
        requestOptionsBodyAuth
      )
        .then((response) => response.blob())
        .then((response) => {
          if (response.type == "application/problem+json") {
            setErrorMessage(
              "Could not load the Electronic Authorization forms, please try again."
            );
          } else {
            setErrorMessage("");
            const pdfBlob = window.URL.createObjectURL(response);
            setEAuthPDFBlob(pdfBlob);
            setLoading(false);
          }
        })
        .catch((error) => {
          setErrorMessage(
            "Could not load the Electronic Authorization forms, please try again."
          );
        });
    } catch (e: unknown) {
      if (utilities.isMaintenanceError(e)) {
        navigate.push("/maint");
      }
    }
  };

  const handlePDFLoad = (pdf: any) => {
    setPDFLoaded(true);
    setNumPages(pdf.numPages);
    setPDFFocus();
  };

  const detectScrollToBottom = (event: any) => {
    const element = event.currentTarget;
    const scrollTop = Math.ceil(element.scrollTop);
    if (scrollTop < lastScrollTop) {
      // upscroll
      return;
    }
    lastScrollTop = scrollTop <= 0 ? 0 : element.scrollTop;
    if (scrollTop + element.offsetHeight >= element.scrollHeight) {
      setScrolledToBottom(true);
    }
  };

  const setPDFFocus = () => {
    const viewerElement = document.querySelector<HTMLElement>('.react-pdf__Document')
    if(!!viewerElement){
      viewerElement.tabIndex = 0;
    }
  }

  const cancel = () => navigate.push("/document-delivery");
  const submit = async () => {
    if (!agreeChecked) {
      setErrorMessage(
        "To continue with your request, you must acknowledge and accept the Electronic Signature and Delivery Authorization."
      );
      return;
    }
    logger.logDetailedActivity('DocumentDelivery', 'eDelivery', cpContext, oktaAuth.getAccessToken() || '', cpContext.userInfo?.email, cpContext.sessionId, "Electronic Authorization Submit", 0);
    setLoading(true);
    const response = await utilities.createEDeliveryFlagWorkObject(oktaAuth, true).then(res => { return res; });
    if (response) {
      setLoading(false);
      navigate.push({ pathname: '/document-delivery', state: {fromEAuth: true} });
    }
  };

  if (loading) {
    return (
      <section aria-labelledby="eauth-section">
        <h1 style={{ display: "none" }}>
          Electronic Signature and Delivery Authorization Spinner
        </h1>
        <h2 style={{ display: "none" }} aria-labelledby="eauth-section">
          Electronic Auth Section
        </h2>
        <Spinner imageWidth={20} aria-label="Loading" />
      </section>
    );
  } else {
    return (
      <div className="row my-5" style={{ padding: 10 }}>
        <div className="col-12">
          <div className="row" style={{ padding: 10 }}>
            <div className="col-12">
              <Card className="policyCard">
                <Card.Body className="shadow">
                  <>
                    <div>
                      <div className="row">
                        <div className="col-12 d-flex justify-content-center">
                          <p>
                            Please review and agree to the Electronic Signature
                            and Delivery Authorization consumer disclosure(s):
                          </p>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-12 d-flex justify-content-center">
                          <p>
                            *NOTE: Your selection will apply to all policies
                          </p>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-12 d-flex justify-content-center">
                          {eAuthPDFBlob && eAuthPDFBlob?.length > 0 ? (
                            <>
                              <Document
                                file={eAuthPDFBlob}
                                onLoadSuccess={handlePDFLoad}
                                options={options}
                                onScroll={detectScrollToBottom}
                                id="ElectronicAuthorizationPreview"
                              >
                                {Array.from(
                                  new Array(numPages),
                                  (el, index) => (
                                    <Page
                                      key={`page_${index + 1}`}
                                      pageNumber={index + 1}
                                      scale="1.5"
                                    />
                                  )
                                )}
                              </Document>
                            </>
                          ) : (
                            <></>
                          )}
                        </div>
                      </div>
                      {pdfLoaded ? (
                        <>
                          <div className="d-flex justify-content-center">
                            <div className="row">
                              <div className="col-12">
                                {errorMessage && <div className="text-danger mt-3">{errorMessage}</div>}
                              </div>
                            </div>
                          </div>
                          <div className="d-flex justify-content-center">
                            <div className="row mt-3">
                              <div
                                className="col-1"
                                style={{ paddingLeft: 30 }}
                              >
                                <input
                                  type="checkbox"
                                  checked={agreeChecked}
                                  value="true"
                                  onChange={(e) => { setAgreeChecked(true); logger.logDetailedActivity('DocumentDelivery', 'Electronic Authorization Checkbox', cpContext, oktaAuth.getAccessToken() || '', cpContext.userInfo?.email, cpContext.sessionId, new Date().toUTCString(), 0); }}
                                  className="AgreeCheck"
                                  aria-label="Agree"
                                ></input>
                              </div>
                              <div
                                className="col-11"
                                style={{ paddingLeft: 30 }}
                              >
                                I acknowledge and accept the Electronic
                                Signature and Delivery Authorization
                              </div>
                            </div>
                          </div>
                          <div className="row pt-4">
                            <div className="col-12 d-flex">
                              <div className="mx-auto">
                                <input
                                  type="button"
                                  className="btn btn-Primary me-2"
                                  onClick={submit}
                                  disabled={!scrolledToBottom}
                                  value="Submit"
                                  aria-label="Submit button"
                                ></input>
                                <input
                                  type="button"
                                  className="btn btn-secondary"
                                  value="Cancel"
                                  onClick={() => { cancel(); logger.logDetailedActivity('DocumentDelivery', 'Cancel', cpContext, oktaAuth.getAccessToken() || '', cpContext.userInfo?.email, cpContext.sessionId, 'Cancel Document Delivery Authorization', 0); }}
                                  aria-label="Cancel button"
                                ></input>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <></>
                      )}
                    </div>
                  </>
                </Card.Body>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default DocumentDeliveryAuthorization;
